(function() {
    'use strict';
  
    const CONFIG = {
      cutoffHour: window.ShippingConfig?.cutoffHour ?? 12,
      holidays: new Set(window.ShippingConfig?.holidays ?? []),
      locale: window.ShippingConfig?.locale ?? 'en'
    };
  
    // Month names are hardcoded rather than taken from toLocaleDateString so
    // that this matches the order confirmation email byte for byte. The email
    // runs in Shopify notification Liquid, which has no locale-aware month
    // names, so it carries the same lists (see docs/emails/order-confirmation-mail).
    // Intl output also varies by platform, which would let the two drift.
    //
    // order      'dm' = day before month (every locale except en)
    // daySuffix  period after the day number (de, fi, no)
    // dash       spaced for en/de/fr, tight for fi/sv/no
    const LOCALES = {
      en: {
        arrives: 'Delivery:', order: 'md', daySuffix: '', dash: ' – ',
        months: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
      },
      de: {
        arrives: 'Lieferung:', order: 'dm', daySuffix: '.', dash: ' – ',
        months: ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez']
      },
      fr: {
        arrives: 'Livraison:', order: 'dm', daySuffix: '', dash: ' – ',
        months: ['janv.','févr.','mars','avr.','mai','juin','juil.','août','sept.','oct.','nov.','déc.']
      },
      fi: {
        arrives: 'Toimitus:', order: 'dm', daySuffix: '.', dash: '–',
        months: ['tammik.','helmik.','maalisk.','huhtik.','toukok.','kesäk.','heinäk.','elok.','syysk.','lokak.','marrask.','jouluk.']
      },
      sv: {
        arrives: 'Leverans:', order: 'dm', daySuffix: '', dash: '–',
        months: ['jan','feb','mars','apr','maj','juni','juli','aug','sep','okt','nov','dec']
      },
      no: {
        arrives: 'Levering:', order: 'dm', daySuffix: '.', dash: '–',
        months: ['jan.','feb.','mars','apr.','mai','juni','juli','aug.','sep.','okt.','nov.','des.']
      }
    };
  
    function isBusinessDay(date) {
      const day = date.getDay();
      if (day === 0 || day === 6) return false;
      // Local calendar date, not toISOString() (UTC), which is a day off east of UTC
      // late in the day and broke the holiday match there.
      const dateString = date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0');
      return !CONFIG.holidays.has(dateString);
    }
  
    function addBusinessDays(startDate, businessDays) {
      const date = new Date(startDate);
      let added = 0;
      while (added < businessDays) {
        date.setDate(date.getDate() + 1);
        if (isBusinessDay(date)) added++;
      }
      return date;
    }
  
    function getEffectiveStartDate() {
      const now = new Date();
      if (now.getHours() >= CONFIG.cutoffHour) {
        now.setDate(now.getDate() + 1);
      }
      while (!isBusinessDay(now)) {
        now.setDate(now.getDate() + 1);
      }
      return now;
    }
  
    // Start day for an order already placed: the order time read in Vienna time
    // (the shop's clock, like the email and the toolbox), rolled past the cutoff
    // and on to a business day. Anchored at local noon so the UTC date that
    // isBusinessDay() checks holidays against is the same calendar day.
    function getStartDateFor(orderedAt) {
      const parts = {};
      new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Vienna', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hour12: false
      }).formatToParts(new Date(orderedAt)).forEach(function (p) { parts[p.type] = p.value; });
      const date = new Date(+parts.year, +parts.month - 1, +parts.day, 12);
      if ((+parts.hour % 24) >= CONFIG.cutoffHour) {
        date.setDate(date.getDate() + 1);
      }
      while (!isBusinessDay(date)) {
        date.setDate(date.getDate() + 1);
      }
      return date;
    }

    // The delivery window promised for an order placed at orderedAt, with the
    // same "start day is day 1" count as calculateShipping() below.
    function rangeFrom(orderedAt, daysMin, daysMax) {
      const startDate = getStartDateFor(orderedAt);
      return {
        min: addBusinessDays(startDate, daysMin - 1),
        max: addBusinessDays(startDate, daysMax - 1)
      };
    }

    // label replaces the locale's "Delivery:" prefix when given.
    function formatDateRange(minDate, maxDate, locale, label) {
      const t = LOCALES[locale] || LOCALES.en;
      const arrives = label === undefined ? t.arrives : label;

      const minDay = minDate.getDate();
      const maxDay = maxDate.getDate();
      const minMonth = t.months[minDate.getMonth()];
      const maxMonth = t.months[maxDate.getMonth()];
      // Compare the year too, so a Dec-to-Dec range across a year boundary is
      // not collapsed into a single month.
      const sameMonth = minDate.getFullYear() === maxDate.getFullYear()
        && minDate.getMonth() === maxDate.getMonth();

      const d = t.daySuffix;
      const dash = t.dash;

      if (t.order === 'dm') {
        if (sameMonth) {
          return `${arrives} ${minDay}${d}${dash}${maxDay}${d} ${maxMonth}`;
        }
        return `${arrives} ${minDay}${d} ${minMonth}${dash}${maxDay}${d} ${maxMonth}`;
      }

      if (sameMonth) {
        return `${arrives} ${minMonth} ${minDay}${dash}${maxDay}`;
      }
      return `${arrives} ${minMonth} ${minDay}${dash}${maxMonth} ${maxDay}`;
    }
  
    function calculateShipping(element) {
      const textElement = element.querySelector('.shipping-estimate-text');
      if (!textElement) return;
  
      const textOverride = element.dataset.textOverride;
      if (textOverride) {
        textElement.textContent = textOverride;
        return;
      }
  
      const daysMin = parseInt(element.dataset.daysMin, 10) || 3;
      const daysMax = parseInt(element.dataset.daysMax, 10) || 5;

      const startDate = getEffectiveStartDate();
      const minDate = addBusinessDays(startDate, daysMin - 1);
      const maxDate = addBusinessDays(startDate, daysMax - 1);
  
      textElement.textContent = formatDateRange(minDate, maxDate, CONFIG.locale);
    }
  
    function init() {
      document.querySelectorAll('.shipping-estimate').forEach(calculateShipping);
    }
  
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  
    document.addEventListener('shopify:section:load', init);
    window.ShippingEstimate = { init, calculateShipping, rangeFrom, formatDateRange };
  })();